import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Shield, Leaf, Star, CheckCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ff8af06a-976d-4efd-824f-28da774a6e41/artifacts/2h81g2hn_use%20pc%20hindi%20logo.png";

// Actual Hing (Asafoetida) Images - Light golden brown color
const HING_IMAGES = {
  bowl: "https://static.prod-images.emergentagent.com/jobs/ff8af06a-976d-4efd-824f-28da774a6e41/images/cd9f10a94af263e218aa96fcc41f3053bda0e23a8389e81b98cc024b18cc0495.png",
  jar: "https://static.prod-images.emergentagent.com/jobs/ff8af06a-976d-4efd-824f-28da774a6e41/images/6697fe7a497ce8a657d75368e4134fd66434b1ee1b69d14f3d1f52fb12bb2a00.png",
  cooking: "https://static.prod-images.emergentagent.com/jobs/ff8af06a-976d-4efd-824f-28da774a6e41/images/8d3d34df708a7f5de29bc09f393fd5b8a6f149ebadab7f4939aa86ad903833bc.png",
  premium: "https://static.prod-images.emergentagent.com/jobs/ff8af06a-976d-4efd-824f-28da774a6e41/images/6164d72ca30ff9a1befa3d1b9d07e77b0d219421a169047d10020919d2b5b5f3.png",
};

const HomePage = () => {
  const products = [
    {
      name: "Premium Hing Powder",
      description: "Finest quality asafoetida powder for authentic Indian cooking",
      weight: "50g / 100g",
      image: HING_IMAGES.bowl,
    },
    {
      name: "Super Hing",
      description: "Extra strong aroma for that perfect tadka",
      weight: "25g / 50g",
      image: HING_IMAGES.jar,
    },
    {
      name: "Hing Granules",
      description: "Easy-to-use granulated form, perfect for everyday cooking",
      weight: "100g / 200g",
      image: HING_IMAGES.cooking,
    },
  ];

  const features = [
    {
      icon: <Award className="w-8 h-8" />,
      title: "Premium Quality",
      description: "100% pure asafoetida sourced from the finest origins"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Lab Tested",
      description: "Every batch tested for purity and quality assurance"
    },
    {
      icon: <Leaf className="w-8 h-8" />,
      title: "Natural & Pure",
      description: "No artificial colors, flavors or preservatives"
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="bg-white min-h-[600px] flex items-center relative border-b-4 border-orange-500" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-50 to-amber-50 opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="animate-fadeInUp">
              <div className="quality-badge mb-6">Premium Quality Hing</div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-gray-900">
                The Authentic Taste of
                <span className="block text-orange-600">Pure Asafoetida</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed max-w-xl">
                Experience the rich aroma and authentic flavor of our premium Hing, 
                crafted to bring the perfect essence to your traditional Indian dishes.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/products" className="btn-primary" data-testid="explore-products-btn">
                  Explore Products
                  <ArrowRight className="inline-block ml-2 w-4 h-4" />
                </Link>
                <Link to="/contact" className="bg-transparent border-2 border-orange-600 text-orange-600 px-8 py-3 rounded font-semibold hover:bg-orange-600 hover:text-white transition-all" data-testid="become-distributor-btn">
                  Become Distributor
                </Link>
              </div>
            </div>

            {/* Right - Logo */}
            <div className="flex justify-center lg:justify-end">
              <div className="animate-float">
                <img 
                  src={LOGO_URL} 
                  alt="Punjabi Chaska" 
                  className="w-72 md:w-96 lg:w-[450px] h-auto drop-shadow-2xl object-contain"
                  data-testid="hero-logo"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="section-title">
            Why <span className="text-brand-orange">Partner With Us</span>
          </h2>
          <p className="section-subtitle mb-12">
            Join hands with Punjabi Chaska for premium quality Hing. 
            We are committed to delivering authentic taste and building lasting business relationships.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-brand-orange mb-2">100%</div>
              <div className="text-gray-600">Pure Quality</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-brand-orange mb-2">B2B</div>
              <div className="text-gray-600">Focused Business</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-brand-orange mb-2">PAN</div>
              <div className="text-gray-600">India Supply</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-brand-cream" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">Why Choose Punjabi Chaska?</h2>
            <p className="section-subtitle">
              Quality is not just our promise, it's our identity
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 text-center"
                data-testid={`feature-card-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-brand-orange mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-white" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">
              Range of <span className="text-brand-orange">Products</span>
            </h2>
            <p className="section-subtitle">
              Indulge in the rich symphony of flavors with our premium Hing range. 
              Our extensive range promises culinary perfection.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div 
                key={index}
                className="product-card"
                data-testid={`product-card-${index}`}
              >
                <div className="h-56 overflow-hidden bg-gradient-to-br from-orange-50 to-orange-100">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Available: {product.weight}</span>
                    <span className="text-brand-orange font-semibold text-sm">Premium</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/products" className="btn-primary" data-testid="view-all-products-btn">
              View All Products
              <ArrowRight className="inline-block ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Quality Promise Section */}
      <section className="py-20 bg-gray-900 text-white" data-testid="quality-promise-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-6">
                Our Quality <span className="text-orange-400">Promise</span>
              </h2>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                At Punjabi Chaska, quality is not just a standard—it's our heritage. 
                Every packet of our Hing undergoes rigorous quality checks to ensure 
                you get nothing but the best.
              </p>
              <ul className="space-y-4">
                {[
                  "100% Pure Asafoetida",
                  "No Artificial Additives",
                  "Hygienically Processed",
                  "Lab Tested for Quality",
                  "Sealed for Freshness"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-orange-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/quality" className="btn-primary mt-8 inline-block" data-testid="quality-page-btn">
                Learn About Our Quality
              </Link>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <img 
                  src={HING_IMAGES.premium} 
                  alt="Quality Cooking"
                  className="rounded-xl shadow-2xl w-full max-w-md"
                />
                <div className="absolute -bottom-6 -right-6 bg-orange-500 text-white p-6 rounded-xl shadow-lg">
                  <div className="flex items-center gap-2">
                    <Star className="w-6 h-6 fill-yellow-300 text-yellow-300" />
                    <span className="text-2xl font-bold">100%</span>
                  </div>
                  <span className="text-sm">Pure & Natural</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info Banner */}
      <section className="py-12 bg-brand-orange" data-testid="company-banner">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-white">
            <div>
              <h3 className="text-2xl font-semibold mb-2">GST Registered Company</h3>
              <p className="text-white/80">
                GSTIN: <span className="font-mono font-bold">09EALPG7629N1ZW</span> | Shri Vishnu Udhyog
              </p>
            </div>
            <Link to="/legal" className="btn-outline" data-testid="view-legal-btn">
              View Legal Info
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="section-title mb-6">Ready to Experience Premium Hing?</h2>
          <p className="section-subtitle mb-10">
            Join thousands of satisfied customers who trust Punjabi Chaska 
            for their daily cooking needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary" data-testid="contact-us-btn">
              Contact Us Today
            </Link>
            <Link to="/products" className="btn-primary bg-gray-900 hover:bg-gray-800" data-testid="browse-products-btn">
              Browse Products
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
